import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { licenseService, getErrorMessage } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Loader2,
  CreditCard,
  DollarSign,
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Building2,
  Key,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LicensePaymentPage() {
  const { licenseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get license data from navigation state or fetch it
  const passedLicense = location.state?.license;
  
  const [license, setLicense] = useState(passedLicense || null);
  const [loading, setLoading] = useState(!passedLicense);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('credit_card'); // credit_card or bank_transfer
  
  // Form state
  const [form, setForm] = useState({
    cardHolderName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    notes: '',
    // Bank transfer fields
    senderName: '',
    senderBank: '',
    transferDate: '',
    transferReference: ''
  });
  
  // Fetch license if not passed
  useEffect(() => {
    if (!passedLicense && licenseId) {
      fetchLicense();
    }
  }, [licenseId, passedLicense]);
  
  // Fetch exchange rate
  useEffect(() => {
    fetchExchangeRate();
  }, []);
  
  const fetchLicense = async () => {
    try {
      setLoading(true);
      const licenses = await licenseService.list();
      const found = licenses.find(l => l.id === licenseId);
      if (found) {
        setLicense(found);
      } else {
        toast.error('Lisans bulunamadı');
        navigate('/licenses');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
      navigate('/licenses');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchExchangeRate = async () => {
    try {
      setRateLoading(true);
      const token = localStorage.getItem('hims_token');
      const response = await fetch(`${API_URL}/api/currency/rates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data);
      }
    } catch (err) {
      console.error('Failed to fetch exchange rate:', err);
    } finally {
      setRateLoading(false);
    }
  };
  
  const getUsdRate = () => {
    if (!exchangeRate?.rates?.USD) return null;
    return exchangeRate.rates.USD.forex_selling || exchangeRate.rates.USD.average;
  };
  
  const calculateTryAmount = () => {
    if (!license?.price || !getUsdRate()) return 0;
    const currency = license.currency || 'USD';
    
    if (currency === 'TRY') {
      return license.price;
    }
    
    if (currency === 'USD') {
      return license.price * getUsdRate();
    }
    
    // For EUR, GBP etc - get their rates
    const rate = exchangeRate?.rates?.[currency];
    if (rate) {
      return license.price * (rate.forex_selling || rate.average || 1);
    }
    
    return license.price;
  };
  
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };
  
  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };
  
  const updateForm = (field, value) => {
    if (field === 'cardNumber') {
      value = formatCardNumber(value);
    } else if (field === 'expiryDate') {
      value = formatExpiryDate(value);
    } else if (field === 'cvv') {
      value = value.replace(/[^0-9]/g, '').substring(0, 4);
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation based on payment method
    if (paymentMethod === 'credit_card') {
      if (!form.cardHolderName.trim()) {
        toast.error('Kart sahibi adı gerekli');
        return;
      }
      if (form.cardNumber.replace(/\s/g, '').length < 16) {
        toast.error('Geçerli bir kart numarası girin');
        return;
      }
      if (!form.expiryDate || form.expiryDate.length < 5) {
        toast.error('Geçerli bir son kullanma tarihi girin');
        return;
      }
      if (!form.cvv || form.cvv.length < 3) {
        toast.error('Geçerli bir CVV girin');
        return;
      }
    } else {
      // Bank transfer validation
      if (!form.senderName.trim()) {
        toast.error('Gönderen adı gerekli');
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('hims_token');
      const tryAmount = calculateTryAmount();
      const usdRate = getUsdRate();
      
      let paymentData;
      
      if (paymentMethod === 'credit_card') {
        const cardLastFour = form.cardNumber.replace(/\s/g, '').slice(-4);
        paymentData = {
          card_holder_name: form.cardHolderName,
          card_last_four: cardLastFour,
          amount_usd: license.currency === 'USD' ? license.price : (tryAmount / usdRate),
          amount_try: tryAmount,
          exchange_rate: usdRate,
          payment_method: 'credit_card',
          notes: form.notes || null
        };
      } else {
        // Bank transfer
        paymentData = {
          card_holder_name: form.senderName,
          card_last_four: null,
          amount_usd: license.currency === 'USD' ? license.price : (tryAmount / usdRate),
          amount_try: tryAmount,
          exchange_rate: usdRate,
          payment_method: 'bank_transfer',
          notes: `Banka: ${form.senderBank || '-'}, Tarih: ${form.transferDate || '-'}, Referans: ${form.transferReference || '-'}. ${form.notes || ''}`
        };
      }
      
      const response = await fetch(`${API_URL}/api/licenses/${licenseId}/payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        setPaymentResult(result);
        if (result.license_extended) {
          toast.success('Ödeme başarılı! Lisansınız 1 yıl uzatıldı.');
        } else {
          toast.success('Havale/EFT kaydı oluşturuldu. Onay bekleniyor.');
        }
      } else {
        toast.error(result.detail || 'Ödeme işlemi başarısız');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (success) {
    const isLicenseExtended = paymentResult?.license_extended;
    const newExpiryDate = paymentResult?.new_expiry_date;
    
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-800 mb-2">
              {isLicenseExtended ? 'Ödeme Başarılı!' : 'Ödeme Kaydı Alındı!'}
            </h2>
            <p className="text-green-700 mb-4">
              {isLicenseExtended 
                ? 'Kredi kartı ödemeniz başarıyla alındı ve lisansınız otomatik olarak uzatıldı.'
                : 'Ödemeniz manuel onay için kaydedildi. Havale/EFT onaylandığında lisansınız yenilenecektir.'
              }
            </p>
            {isLicenseExtended && newExpiryDate && (
              <div className="bg-white p-4 rounded-lg mb-6 inline-block">
                <p className="text-gray-600 text-sm">Yeni Bitiş Tarihi:</p>
                <p className="text-2xl font-bold text-green-700">
                  {new Date(newExpiryDate).toLocaleDateString('tr-TR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            )}
            <div className="flex justify-center gap-3">
              <Button onClick={() => navigate('/licenses')}>
                Lisanslara Dön
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Dashboard'a Git
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const tryAmount = calculateTryAmount();
  const usdRate = getUsdRate();
  const currencySymbol = { USD: '$', EUR: '€', TRY: '₺', GBP: '£' }[license?.currency] || '$';
  
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="license-payment-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/licenses')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lisans Ödemesi</h1>
          <p className="text-gray-500">Kredi kartı ile güvenli ödeme</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* License Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Lisans Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Lisans Anahtarı:</span>
                <span className="font-mono font-medium">{license?.license_key}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Bitiş Tarihi:</span>
                <span className="font-medium text-red-600">
                  {license?.expires_at 
                    ? new Date(license.expires_at).toLocaleDateString('tr-TR')
                    : '-'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Maks İstasyon:</span>
                <span className="font-medium">{license?.max_stations}</span>
              </div>
            </div>
            
            {/* Price Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-blue-800 font-medium">Lisans Ücreti:</span>
                <span className="text-2xl font-bold text-blue-900">
                  {currencySymbol}{(license?.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </span>
              </div>
              
              {/* Exchange Rate Info */}
              {license?.currency !== 'TRY' && (
                <div className="pt-3 border-t border-blue-200 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-700 flex items-center gap-1">
                      <TrendingUp className="h-4 w-4" />
                      TCMB Kuru ({license?.currency}/TRY):
                    </span>
                    {rateLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          ₺{usdRate?.toLocaleString('tr-TR', { minimumFractionDigits: 4 })}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-6 w-6"
                          onClick={fetchExchangeRate}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {exchangeRate?.source === 'FALLBACK' && (
                    <Alert className="py-2">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        {exchangeRate.warning}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-blue-800 font-medium">TL Karşılığı:</span>
                    <span className="text-xl font-bold text-green-700">
                      ₺{tryAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  
                  {exchangeRate?.date && (
                    <p className="text-xs text-blue-600 text-right">
                      Kur tarihi: {exchangeRate.date}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Payment Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Ödeme Yöntemi
            </CardTitle>
            <CardDescription>
              Kredi kartı veya havale/EFT ile ödeme yapabilirsiniz
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Payment Method Selection */}
            <div className="flex gap-2 mb-6">
              <Button
                type="button"
                variant={paymentMethod === 'credit_card' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setPaymentMethod('credit_card')}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Kredi Kartı
              </Button>
              <Button
                type="button"
                variant={paymentMethod === 'bank_transfer' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setPaymentMethod('bank_transfer')}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Havale/EFT
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {paymentMethod === 'credit_card' ? (
                <>
                  {/* Credit Card Form */}
                  <div className="space-y-2">
                    <Label htmlFor="cardHolderName">Kart Sahibi Adı *</Label>
                    <Input
                      id="cardHolderName"
                      value={form.cardHolderName}
                      onChange={(e) => updateForm('cardHolderName', e.target.value.toUpperCase())}
                      placeholder="AD SOYAD"
                      className="uppercase"
                      data-testid="card-holder-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cardNumber">Kart Numarası *</Label>
                    <div className="relative">
                      <Input
                        id="cardNumber"
                        value={form.cardNumber}
                        onChange={(e) => updateForm('cardNumber', e.target.value)}
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                        className="pl-10"
                        data-testid="card-number-input"
                      />
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expiryDate">Son Kullanma *</Label>
                      <Input
                        id="expiryDate"
                        value={form.expiryDate}
                        onChange={(e) => updateForm('expiryDate', e.target.value)}
                        placeholder="AA/YY"
                        maxLength={5}
                        data-testid="expiry-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cvv">CVV *</Label>
                      <Input
                        id="cvv"
                        type="password"
                        value={form.cvv}
                        onChange={(e) => updateForm('cvv', e.target.value)}
                        placeholder="123"
                        maxLength={4}
                        data-testid="cvv-input"
                      />
                    </div>
                  </div>
                  
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">
                      Kredi kartı ile ödeme yapıldığında lisansınız <strong>otomatik olarak 1 yıl uzatılır</strong>.
                    </AlertDescription>
                  </Alert>
                </>
              ) : (
                <>
                  {/* Bank Transfer Form */}
                  <Alert className="bg-yellow-50 border-yellow-200 mb-4">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-700">
                      Havale/EFT ödemeleri <strong>manuel onay</strong> gerektirir. Ödemeniz onaylandığında lisansınız uzatılacaktır.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                    <h4 className="font-medium text-blue-800 mb-2">Havale/EFT Bilgileri</h4>
                    <div className="space-y-1 text-sm">
                      <p><strong>Banka:</strong> Ziraat Bankası</p>
                      <p><strong>Şube:</strong> Merkez Şube (1234)</p>
                      <p><strong>Hesap Adı:</strong> HIMS Teknoloji A.Ş.</p>
                      <p><strong>IBAN:</strong> TR00 0000 0000 0000 0000 0000 00</p>
                      <p className="text-blue-600 mt-2">Açıklama kısmına lisans anahtarınızı yazınız.</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="senderName">Gönderen Adı *</Label>
                    <Input
                      id="senderName"
                      value={form.senderName}
                      onChange={(e) => updateForm('senderName', e.target.value.toUpperCase())}
                      placeholder="AD SOYAD"
                      className="uppercase"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="senderBank">Gönderen Banka</Label>
                      <Input
                        id="senderBank"
                        value={form.senderBank}
                        onChange={(e) => updateForm('senderBank', e.target.value)}
                        placeholder="Banka adı"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="transferDate">Transfer Tarihi</Label>
                      <Input
                        id="transferDate"
                        type="date"
                        value={form.transferDate}
                        onChange={(e) => updateForm('transferDate', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="transferReference">Dekont/Referans No</Label>
                    <Input
                      id="transferReference"
                      value={form.transferReference}
                      onChange={(e) => updateForm('transferReference', e.target.value)}
                      placeholder="Dekont numarası veya referans"
                    />
                  </div>
                </>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="notes">Not (Opsiyonel)</Label>
                <Input
                  id="notes"
                  value={form.notes}
                  onChange={(e) => updateForm('notes', e.target.value)}
                  placeholder="Ödeme ile ilgili not..."
                />
              </div>
              
              {/* Summary */}
              <div className="p-4 bg-gray-100 rounded-lg mt-4">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Ödenecek Tutar:</span>
                  <span className="text-green-700">
                    ₺{tryAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ({currencySymbol}{license?.price?.toLocaleString('tr-TR')} × {usdRate?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} kur)
                </p>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 text-lg"
                disabled={submitting || rateLoading}
                data-testid="submit-payment-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    İşleniyor...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Ödemeyi Kaydet
                  </>
                )}
              </Button>
              
              <p className="text-xs text-gray-500 text-center">
                Bu ödeme manuel onay gerektirir. Onaylandıktan sonra lisansınız otomatik olarak 1 yıl uzatılacaktır.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
